import React from "react";
import {
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import DeleteIcon from '@mui/icons-material/Delete';

import { UserAvatar } from "../../../components/avatars";
import StatusChip from "../../../components/chips/status-chip";
import EditableRoleChip from "../../../components/chips/editable-role-chip";
import { User, UserType } from "../../../types";
import { userHasPermission } from "../../../types-helpers";
import TextCopyChip from "../../../components/chips/text-copy-chip";

interface AdminUsersTableRowProps {
  user: User;
  currentUserId: string;
  selectedClientId?: string;
  blockUserClick: (user: User) => void;
}

const AdminUsersTableRow: React.FC<AdminUsersTableRowProps> = ({
  user,
  currentUserId,
  selectedClientId,
  blockUserClick,
}) => {
  const formatLastLogin = (user: User) => {
    if (!user.lastLogin) {
      if (user.userType == UserType.Guest) {
        return "---";
      }

      return "Never";
    }

    const now = new Date();
    const diffMs = now.getTime() - user.lastLogin.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 24) {
      return "Today";
    } else if (diffHours < 168) {
      // 7 days
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    } else {
      return user.lastLogin.toLocaleDateString();
    }
  };

  const canDelete =
    user.userId != currentUserId && userHasPermission(UserType.Guest, user) && user.lastLogin == null;

  const canBlock = user.userId != currentUserId;

  const block = user.userType != UserType.Blocked;

  const getStatus = () => {
    if (user.userType === UserType.Blocked) return "Blocked";
    if (user.userType === UserType.Inactive) return "Inactive";
    return "Active";
  };

  const getStatusChipValue = (): "Active" | "Inactive" | "Banned" => {
    if (status === "Blocked") {
      return "Banned";
    }

    if (status === "Inactive") {
      return "Inactive";
    }

    return "Active";
  };

  const status = getStatus();
  const otherClients = (user.allClientIds ?? []).filter(
    (clientId) => clientId !== selectedClientId,
  );

  return (
    <TableRow
      key={user.userId}
      sx={{
        cursor: "pointer",
        "&:hover": {
          background:
            "linear-gradient(135deg, rgba(5, 84, 128, 0.08) 0%, rgba(0, 150, 136, 0.08) 100%)",
        },
      }}
    >
      <TableCell sx={{ py: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <UserAvatar
            text={user.fullName || user.email || "User"}
            sx={{
              width: 24,
              height: 24,
              fontSize: "0.65rem",
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight="medium">
              {user.fullName}
            </Typography>
          </Stack>
        </Stack>
      </TableCell>

      <TableCell align="center" sx={{ py: 1 }}>
        <EditableRoleChip user={user} chipHeight="18px" chipFontSize="0.7rem" />
      </TableCell>

      <TableCell align="center" sx={{ py: 1 }}>
        <StatusChip
          status={getStatusChipValue()}
          size="small"
          height="18px"
          fontSize="0.7rem"
          label={status}
        />
      </TableCell>

      <TableCell sx={{ py: 1 }}>
        {user.email ? (
          <TextCopyChip text={user.email} />
        ) : (
          <Typography variant="body2" color="text.disabled">
            No email
          </Typography>
        )}
      </TableCell>

      <TableCell align="center" sx={{ py: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {formatLastLogin(user)}
        </Typography>
      </TableCell>

      <TableCell sx={{ py: 1 }}>
        {otherClients.length > 0 ? (
          <TextCopyChip text={otherClients.join(", ")} />
        ) : (
          <Typography variant="body2" color="text.disabled">
            None
          </Typography>
        )}
      </TableCell>

      <TableCell align="right" sx={{ py: 1 }}>
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {/* TODO rethink... this should cause the deletion of a user from the platform, and can only be done if never logged in */}
          {/* <IconButton
            size="small"
            title="Delete User"
            color="error"
            sx={{ padding: "4px" }}
            onClick={() => deleteClick(user)}
            disabled={!canDelete}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton> */}

          <IconButton
            size="small"
            title="Block User"
            color="error"
            sx={{ padding: "4px" }}
            onClick={() => blockUserClick(user)}
            disabled={!canBlock}
          >
            {block && <NoAccountsIcon sx={{ fontSize: 16 }} />}
            {!block && <SettingsBackupRestoreIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default AdminUsersTableRow;
